import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { checkEnvironment } from '@saviynt/common';
import {
  ButtonIcon,
  Icon,
  InputField,
  MenuNested,
  Typography,
} from '@saviynt/design-system';

import useInfiniteForms from '../../../../../hooks/use-infinite-forms';
import { useDebounce } from '../../../../../utils';
import { DEBOUNCE_DELAY } from '../../../../../utils/constants';
import { USER_REQUEST_CATEGORY } from '../../../constants';
import { type Form } from '../../../types';

interface EnvConfig {
  GATEWAY_API_URL?: string;
}

function UserActionMenu({ userkey }: { userkey: number | undefined }) {
  const [searchFormKeyword, setSearchFormKeyword] = useState('');
  const [isNestedOpen, setIsNestedOpen] = useState(false);
  const debouncedValue = useDebounce(searchFormKeyword, DEBOUNCE_DELAY);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteForms({
      category: USER_REQUEST_CATEGORY.UPDATE,
      searchText: debouncedValue,
      formCategory: '',
      formTags: '',
      urlSlug: '',
      description: '',
      size: 20,
      sortBy: 'id',
      sortOrder: 'desc',
      status: ['PUBLISHED'],
      createUser: [],
      updateUser: [],
    });

  const allForms = useMemo(
    () =>
      isNestedOpen
        ? data?.pages
            .flatMap((page) => page.data)
            .filter((form) => form.auth) ?? []
        : [],
    [data, isNestedOpen]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage().catch(() => {});
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, allForms.length]);

  const navigateToForm = useCallback(
    (form: Form) => {
      const env = checkEnvironment() as EnvConfig;
      const baseUrl = `${env.GATEWAY_API_URL ?? ''}/forms/${
        form.customFormUid
      }/${form.urlSlug}`;

      window.location.href = `${baseUrl}/update/${userkey ?? ''}`;
    },
    [userkey]
  );

  return (
    <MenuNested
      trigger={
        <ButtonIcon
          size='medium'
          kind='ghost'
          icon={<Icon kind='MoreVertical' />}
          ariaLabel='Row actions'
        />
      }
      placement='bottom-start'>
      <MenuNested
        label='Update'
        placement='right-start'
        setIsExpanded={setIsNestedOpen}
        classNames={{
          dropdownClassName: 'UsersSortableList-nestedMenuSmall',
          itemClassName: 'UsersSortableList-nestedUpdateItem',
        }}>
        <li className='UserManagement-nestedSearch'>
          <InputField
            name='searchForms'
            size='small'
            placeholder='Search'
            kind='search'
            prefixIcon={
              <Icon kind='Search' color='--color-foreground-neutral-subtle' />
            }
            value={searchFormKeyword}
            setValue={setSearchFormKeyword}
          />
        </li>
        <li className='UserManagement-nestedScrollList'>
          {allForms.map((form: Form) => (
            <div key={form.id} className='UserManagement-formItem'>
              <Typography
                kind='body2'
                color='--color-foreground-secondary-bold'
                lineLimit={1}
                tooltipConfigs={{ isPortalEnabled: true }}>
                {form.name ?? 'Untitled Form'}
              </Typography>
              <ButtonIcon
                size='small'
                kind='ghost'
                icon={<Icon kind='ArrowRight' />}
                ariaLabel={`Open ${form.name ?? 'form'}`}
                onClick={() => navigateToForm(form)}
              />
            </div>
          ))}
          <div ref={sentinelRef} style={{ height: '1px' }} />
        </li>
      </MenuNested>
    </MenuNested>
  );
}

export default UserActionMenu;
